import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, Copy, Check, Image as ImageIcon, RefreshCw, Wand2 } from "lucide-react";
import { api } from "@/lib/api";

const TYPES = [
  { k: "red_flags", label: "🚩 Carrousel Red Flags" },
  { k: "verdict", label: "🎢 Verdict choc" },
  { k: "tip", label: "💡 Astuce couple" },
  { k: "quiz", label: "❓ Quiz / sondage" },
  { k: "feature", label: "📱 Fonctionnalité DocLove" },
  { k: "myth", label: "✅ Vrai ou Faux" },
  { k: "story", label: "📖 Mini-histoire" },
];

const GRADS = [
  ["#1a0b2e", "#3b0764"],
  ["#2a0a1e", "#7f1d3a"],
  ["#0b1026", "#3b1d6e"],
];

function Slide({ slide, index, total, isFirst, isLast, cta, slideRef }) {
  const g = GRADS[index % GRADS.length];
  return (
    <div
      ref={slideRef}
      style={{
        width: 1080, height: 1350,
        background: `radial-gradient(120% 90% at 20% 0%, ${g[1]} 0%, ${g[0]} 60%, #09090E 100%)`,
        fontFamily: "'Outfit','Manrope',sans-serif",
      }}
      className="relative flex flex-col justify-between p-20 text-white shrink-0"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div style={{ width: 64, height: 64 }} className="rounded-2xl bg-gradient-to-br from-rose-500 to-purple-600 flex items-center justify-center text-4xl">💘</div>
          <span style={{ fontSize: 34 }} className="font-extrabold tracking-tight">DocLove</span>
        </div>
        <span style={{ fontSize: 28 }} className="font-bold text-white/40">{index + 1}/{total}</span>
      </div>

      <div className="flex-1 flex flex-col justify-center">
        {isFirst ? (
          <p style={{ fontSize: 96, lineHeight: 1.05 }} className="font-black tracking-tight">
            {slide.body || slide.title}
          </p>
        ) : (
          <>
            <p style={{ fontSize: 58, lineHeight: 1.1 }} className="font-black tracking-tight mb-8 text-transparent bg-clip-text" >
              <span style={{ backgroundImage: "linear-gradient(120deg,#fb7185,#e11d48,#a855f7)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
                {slide.title}
              </span>
            </p>
            <p style={{ fontSize: 44, lineHeight: 1.35 }} className="font-medium text-white/90">{slide.body}</p>
          </>
        )}
      </div>

      <div className="flex items-center justify-between">
        <span style={{ fontSize: 30 }} className="font-semibold text-white/50">@doclove.app</span>
        {isFirst ? (
          <span style={{ fontSize: 30 }} className="font-bold text-white/70">Swipe →</span>
        ) : isLast ? (
          <span style={{ fontSize: 30 }} className="font-black text-rose-300">{cta || "Teste DocLove 💘"}</span>
        ) : null}
      </div>
    </div>
  );
}

export default function ContentStudio() {
  const [type, setType] = useState("red_flags");
  const [tone, setTone] = useState("cash");
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState(null);
  const [copied, setCopied] = useState("");
  const [img, setImg] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const slideRefs = useRef([]);

  const generate = async () => {
    setLoading(true); setPost(null); setImg(null);
    try {
      const r = await api.post("/admin/content/generate", { type, tone, topic: topic.trim() || null, slides: 6 });
      setPost(r.data.data);
      toast.success("Contenu généré ✨");
    } catch { toast.error("Génération échouée"); }
    finally { setLoading(false); }
  };

  const genImage = async () => {
    if (!post?.image_prompt) return;
    setImgLoading(true);
    try {
      const r = await api.post("/admin/content/image", { prompt: post.image_prompt });
      setImg(r.data.image);
      toast.success("Image générée 🎨");
    } catch { toast.error("Image échouée"); }
    finally { setImgLoading(false); }
  };

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch {}
  };

  const downloadSlide = async (i) => {
    const node = slideRefs.current[i];
    if (!node) return;
    try {
      const dataUrl = await toPng(node, { pixelRatio: 1, cacheBust: true, skipFonts: false, width: 1080, height: 1350 });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `doclove-slide-${i + 1}.png`; a.click();
    } catch { toast.error("Export échoué"); }
  };

  const downloadImage = () => {
    if (!img) return;
    const a = document.createElement("a");
    a.href = img; a.download = "doclove-cover.png"; a.click();
  };

  const slides = post?.slides || [];

  return (
    <div className="space-y-5" data-testid="content-studio">
      <div className="rounded-2xl bg-gradient-to-br from-rose-500/10 to-purple-500/10 border border-white/10 p-4">
        <p className="text-sm font-bold flex items-center gap-2 mb-1"><Wand2 size={16} className="text-rose-400" /> Studio de contenu Instagram</p>
        <p className="text-[11px] text-zinc-400">Génère des posts prêts à publier (carrousels, verdicts, astuces…) avec visuels IA. Télécharge les slides en PNG et poste sur ton compte.</p>
      </div>

      <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3">
        <div>
          <label className="text-xs text-zinc-400">Type de post</label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {TYPES.map((t) => (
              <button key={t.k} data-testid={`content-type-${t.k}`} onClick={() => setType(t.k)}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${type === t.k ? "bg-rose-500/15 border-rose-500/40 text-rose-200" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="text-xs text-zinc-400">Ton</label>
            <div className="flex gap-2 mt-1.5">
              {[["cash", "😜 Cash / viral"], ["premium", "🎩 Premium / expert"]].map(([k, l]) => (
                <button key={k} data-testid={`content-tone-${k}`} onClick={() => setTone(k)}
                  className={`flex-1 text-xs font-semibold rounded-lg px-3 py-2 border transition-colors ${tone === k ? "bg-purple-500/15 border-purple-500/40 text-purple-200" : "border-zinc-700 text-zinc-400"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs text-zinc-400">Sujet précis (optionnel)</label>
          <input data-testid="content-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
            placeholder="ex: le love bombing, le ghosting, comment relancer un crush…"
            className="w-full mt-1.5 rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm focus:border-rose-500/50 outline-none" />
        </div>
        <button data-testid="content-generate" onClick={generate} disabled={loading}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-600 to-purple-600 text-white font-semibold py-3 disabled:opacity-50 active:scale-[0.98] transition-transform">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Génération…</> : <><Sparkles size={16} /> Générer le post</>}
        </button>
      </div>

      {post && (
        <div className="space-y-4" data-testid="content-result">
          {/* Carousel preview */}
          <div>
            <p className="text-xs font-bold text-zinc-400 mb-2">Carrousel ({slides.length} slides) — télécharge chaque slide</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {slides.map((s, i) => (
                <div key={i} className="shrink-0">
                  <div className="rounded-xl overflow-hidden border border-zinc-800" style={{ width: 180, height: 225 }}>
                    <div style={{ transform: "scale(0.16667)", transformOrigin: "top left" }}>
                      <Slide slide={s} index={i} total={slides.length} isFirst={i === 0} isLast={i === slides.length - 1} cta={post.cta}
                        slideRef={(el) => (slideRefs.current[i] = el)} />
                    </div>
                  </div>
                  <button data-testid={`content-download-${i}`} onClick={() => downloadSlide(i)}
                    className="mt-1.5 w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-zinc-300 hover:text-white rounded-lg bg-zinc-800/60 py-1.5">
                    <Download size={12} /> Slide {i + 1}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* AI cover image */}
          <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-zinc-400 flex items-center gap-1.5"><ImageIcon size={14} /> Visuel IA (optionnel)</p>
              <button data-testid="content-gen-image" onClick={genImage} disabled={imgLoading}
                className="text-xs font-semibold rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-200 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
                {imgLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {img ? "Régénérer" : "Générer l'image"}
              </button>
            </div>
            {img ? (
              <div>
                <img src={img} alt="visuel généré" className="w-full rounded-xl border border-zinc-800" />
                <button onClick={downloadImage} className="mt-2 w-full flex items-center justify-center gap-1 text-xs font-semibold text-zinc-300 rounded-lg bg-zinc-800/60 py-2"><Download size={13} /> Télécharger l'image</button>
              </div>
            ) : (
              <p className="text-[11px] text-zinc-600">Génère une image de couverture IA à partir du concept du post (à mixer avec tes screenshots de l'app).</p>
            )}
          </div>

          {/* Caption + hashtags */}
          <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4 space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold text-zinc-400">Légende</p>
                <button data-testid="content-copy-caption" onClick={() => copy(post.caption, "cap")} className="text-xs text-rose-300 flex items-center gap-1">
                  {copied === "cap" ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
                </button>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{post.caption}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold text-zinc-400">Hashtags</p>
                <button data-testid="content-copy-hashtags" onClick={() => copy((post.hashtags || []).join(" "), "tags")} className="text-xs text-rose-300 flex items-center gap-1">
                  {copied === "tags" ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
                </button>
              </div>
              <p className="text-xs text-sky-300">{(post.hashtags || []).join("  ")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
